/**
 * Versiones normativas: cargar, ver el impacto y publicar.
 *
 * Esto no existía en ninguna pantalla. Cargar y publicar el RIPCI es lo que
 * hace que el sistema calcule algo —sin una versión publicada no hay reglas
 * activas, no se genera ningún plan y no vence nada—, y hasta ahora había que
 * llamar a la API a mano sabiéndose las rutas.
 *
 * El botón de publicar exige haber visto el informe de impacto. No es un
 * capricho de interfaz: la ADR-007 dice que publicar a ciegas sobre miles de
 * equipos no es aceptable, y publicar es irreversible —la versión queda
 * inmutable y sus reglas empiezan a generar obligaciones para todo el parque—.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';

interface Version {
  id: string;
  regulation_code: string;
  regulation_name: string;
  version_label: string;
  boe_reference: string;
  source_url: string;
  valid_from: string;
  valid_to: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
  rule_count: number;
}

interface Impact {
  versionId: string;
  rules: {
    // eslint-disable-next-line no-restricted-syntax -- recuento de reglas, no un importe
    total: number;
    added: string[];
    changed: string[];
    removed: string[];
  };
  pendingUneAcquisition: string[];
  affectedEquipment: number;
}

const STATUS_LABEL: Record<Version['status'], string> = {
  DRAFT: 'Borrador',
  PUBLISHED: 'Publicada',
  SUPERSEDED: 'Sustituida',
};

export function RegulationVersions(): React.ReactNode {
  const { api, user } = useSession();

  const [versions, setVersions] = useState<Version[]>([]);
  const [impact, setImpact] = useState<Impact | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const canLoad = hasPermission(user, 'regulation.create');
  const canPublish = hasPermission(user, 'regulation.publish');
  const canSeeImpact = hasPermission(user, 'regulation.impact_report');

  const reload = useCallback(async () => {
    const data = await api.get<{ items: Version[] }>('/regulations/versions');
    setVersions(data.items);
  }, [api]);

  useEffect(() => {
    reload().catch((caught: unknown) => {
      setError(caught instanceof ApiError ? caught.message : 'No se han podido cargar las versiones.');
    });
  }, [reload]);

  async function loadRipci(): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const result = await api.post<{ rules: number; systems: number; alreadyLoaded: boolean }>(
        '/regulations/load/ripci',
      );
      setNotice(
        result.alreadyLoaded
          ? 'El RIPCI ya estaba cargado. No se ha duplicado nada.'
          : `Cargado en borrador: ${result.systems} sistemas y ${result.rules} reglas. Revise el impacto antes de publicar.`,
      );
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar la normativa.');
    } finally {
      setBusy(false);
    }
  }

  async function showImpact(version: Version): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      setImpact(await api.get<Impact>(`/regulations/versions/${version.id}/impact`));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido calcular el impacto.');
    } finally {
      setBusy(false);
    }
  }

  async function publish(version: Version): Promise<void> {
    const affected = impact?.affectedEquipment ?? 0;
    if (
      !globalThis.confirm(
        `¿Publicar ${version.regulation_code} ${version.version_label}?\n\n` +
          `${version.rule_count} reglas empezarán a generar obligaciones` +
          (affected > 0 ? ` sobre ${affected} equipos.` : ' en cuanto haya equipos.') +
          '\n\nEs IRREVERSIBLE: la versión queda inmutable.',
      )
    ) {
      return;
    }

    setError(null);
    setBusy(true);
    try {
      await api.post(`/regulations/versions/${version.id}/publish`);
      setNotice(`${version.regulation_code} ${version.version_label} publicada.`);
      setImpact(null);
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido publicar.');
    } finally {
      setBusy(false);
    }
  }

  const published = versions.some((v) => v.status === 'PUBLISHED');

  return (
    <section className="panel">
      <h3 className="panel__title">Versiones normativas</h3>

      {!published && (
        <p className="screen__note screen__note--strong">
          No hay ninguna versión publicada. Hasta que la haya, el sistema no genera planes de
          mantenimiento ni vencimientos: las reglas existen pero no se aplican a nada.
        </p>
      )}

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}

      {versions.length === 0 ? (
        <p className="screen__note">Todavía no se ha cargado ninguna normativa.</p>
      ) : (
        <div className="scroller">
          <table className="table__grid table__grid--compact">
            <thead>
              <tr>
                <th>Reglamento</th>
                <th>Versión</th>
                <th>Referencia del BOE</th>
                <th>Vigencia</th>
                <th>Reglas</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((version) => (
                <tr key={version.id}>
                  <td title={version.regulation_name}>{version.regulation_code}</td>
                  <td>{version.version_label}</td>
                  <td>
                    <a href={version.source_url} target="_blank" rel="noreferrer">
                      {version.boe_reference}
                    </a>
                  </td>
                  <td>
                    {version.valid_from}
                    {version.valid_to ? ` → ${version.valid_to}` : ''}
                  </td>
                  <td>{version.rule_count}</td>
                  <td>
                    {version.status === 'PUBLISHED' ? (
                      <span className="pill pill--ok">✓ {STATUS_LABEL[version.status]}</span>
                    ) : version.status === 'DRAFT' ? (
                      <span className="pill pill--info">{STATUS_LABEL[version.status]}</span>
                    ) : (
                      <span className="pill pill--off">{STATUS_LABEL[version.status]}</span>
                    )}
                  </td>
                  <td>
                    {version.status === 'DRAFT' && canSeeImpact && (
                      <>
                        <button
                          className="button button--quiet"
                          type="button"
                          disabled={busy}
                          onClick={() => void showImpact(version)}
                        >
                          Ver impacto
                        </button>{' '}
                      </>
                    )}
                    {version.status === 'DRAFT' && canPublish && (
                      <button
                        className="button button--primary"
                        type="button"
                        // Publicar sin haber mirado el impacto es justo lo que
                        // la ADR-007 prohíbe. El botón espera a que se vea.
                        disabled={busy || impact?.versionId !== version.id}
                        title={
                          impact?.versionId === version.id
                            ? undefined
                            : 'Vea antes el informe de impacto'
                        }
                        onClick={() => void publish(version)}
                      >
                        Publicar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {impact !== null && (
        <div className="panel">
          <h3 className="panel__title">Informe de impacto</h3>
          <ul className="attachments">
            <li className="attachments__item">
              <span className="attachments__name">Reglas en la versión</span>
              <span className="attachments__meta">{impact.rules.total}</span>
            </li>
            <li className="attachments__item">
              <span className="attachments__name">Nuevas · modificadas · eliminadas</span>
              <span className="attachments__meta">
                {impact.rules.added.length} · {impact.rules.changed.length} ·{' '}
                {impact.rules.removed.length}
              </span>
            </li>
            <li className="attachments__item">
              <span className="attachments__name">Equipos afectados</span>
              <span className="attachments__meta">{impact.affectedEquipment}</span>
            </li>
            <li className="attachments__item">
              <span className="attachments__name">
                Reglas pendientes de adquirir la norma UNE (decisión P-01)
              </span>
              <span className="attachments__meta">{impact.pendingUneAcquisition.length}</span>
            </li>
          </ul>
          {impact.pendingUneAcquisition.length > 0 && (
            <p className="screen__note">
              Se publican <strong>identificadas como pendientes</strong>, con el texto del BOE, no
              reconstruidas con información no fiable: {impact.pendingUneAcquisition.join(', ')}.
            </p>
          )}
        </div>
      )}

      {canLoad && (
        <p>
          <button className="button button--quiet" type="button" disabled={busy} onClick={() => void loadRipci()}>
            {busy ? 'Trabajando…' : 'Cargar RIPCI 2025.1'}
          </button>{' '}
          <span className="screen__note">
            Idempotente: si ya está cargado, no duplica nada.
          </span>
        </p>
      )}
    </section>
  );
}
